import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listServiceEventLineItems,
  upsertServiceEventLineItem,
} from "@/lib/service/client";
import { lineItemAmountCents } from "@/lib/service/eventLineItemMath";
import type { ServiceEvent, ServiceEventLineItem, ServiceEventLineItemCategory } from "@/lib/service/types";

const EDITABLE_STATUSES = new Set<ServiceEvent["status"]>(["in_progress", "complete"]);

export type EventLineItemDraft = {
  id?: string;
  category: ServiceEventLineItemCategory;
  label: string;
  quantity: string;
  pricing_mode: "unit" | "flat";
  unit_dollars: string;
  amount_dollars: string;
};

export function emptyLineItemDraft(): EventLineItemDraft {
  return {
    category: "labor",
    label: "",
    quantity: "1",
    pricing_mode: "unit",
    unit_dollars: "",
    amount_dollars: "",
  };
}

function draftFromLine(line: ServiceEventLineItem): EventLineItemDraft {
  if (line.amount_cents != null) {
    return {
      id: line.id,
      category: line.category,
      label: line.label,
      quantity: String(line.quantity),
      pricing_mode: "flat",
      unit_dollars: "",
      amount_dollars: (line.amount_cents / 100).toFixed(2),
    };
  }
  return {
    id: line.id,
    category: line.category,
    label: line.label,
    quantity: String(line.quantity),
    pricing_mode: "unit",
    unit_dollars: line.unit_cents != null ? (line.unit_cents / 100).toFixed(2) : "",
    amount_dollars: "",
  };
}

function parseDollarsToCents(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

export function useServiceEventLineItems(event: ServiceEvent | null) {
  const [lines, setLines] = useState<ServiceEventLineItem[]>([]);
  const [subtotalCents, setSubtotalCents] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EventLineItemDraft>(emptyLineItemDraft);

  const editable = event != null && EDITABLE_STATUSES.has(event.status);

  const reload = useCallback(async () => {
    if (!event) {
      setLines([]);
      setSubtotalCents(0);
      return;
    }
    setBusy(true);
    setError(null);
    const res = await listServiceEventLineItems(event.id);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      setLines([]);
      setSubtotalCents(0);
      return;
    }
    setLines(res.data.lines);
    setSubtotalCents(res.data.subtotal_cents);
  }, [event]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    setDraft(emptyLineItemDraft());
  }, [event?.id]);

  const localSubtotalCents = useMemo(() => {
    return lines.reduce((sum, line) => sum + lineItemAmountCents(line), 0);
  }, [lines]);

  const saveDraft = useCallback(async () => {
    if (!event || !editable) return false;
    const label = draft.label.trim();
    if (!label) {
      setError("Label is required.");
      return false;
    }
    const quantity = Number(draft.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError("Quantity must be a positive number.");
      return false;
    }

    let unit_cents: number | null = null;
    let amount_cents: number | null = null;
    if (draft.pricing_mode === "flat") {
      amount_cents = parseDollarsToCents(draft.amount_dollars);
      if (amount_cents == null) {
        setError("Enter a flat amount in USD.");
        return false;
      }
    } else {
      unit_cents = parseDollarsToCents(draft.unit_dollars);
      if (unit_cents == null) {
        setError("Enter a unit price in USD.");
        return false;
      }
    }

    setActionBusy(true);
    setError(null);
    const res = await upsertServiceEventLineItem({
      id: draft.id,
      event_id: event.id,
      category: draft.category,
      label,
      quantity,
      unit_cents,
      amount_cents,
    });
    setActionBusy(false);
    if (!res.ok) {
      setError(res.error);
      return false;
    }
    setDraft(emptyLineItemDraft());
    await reload();
    return true;
  }, [draft, editable, event, reload]);

  const removeLine = useCallback(
    async (lineId: string) => {
      if (!event || !editable) return false;
      setActionBusy(true);
      setError(null);
      const res = await upsertServiceEventLineItem({
        event_id: event.id,
        category: "other",
        label: "-",
        delete: true,
        id: lineId,
      });
      setActionBusy(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      if (draft.id === lineId) setDraft(emptyLineItemDraft());
      await reload();
      return true;
    },
    [draft.id, editable, event, reload]
  );

  const editLine = useCallback((line: ServiceEventLineItem) => {
    setDraft(draftFromLine(line));
    setError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setDraft(emptyLineItemDraft());
    setError(null);
  }, []);

  return {
    lines,
    subtotalCents: subtotalCents || localSubtotalCents,
    busy,
    actionBusy,
    error,
    editable,
    draft,
    setDraft,
    saveDraft,
    removeLine,
    editLine,
    cancelEdit,
    reload,
  };
}
